import { Action, createReducer, on } from '@ngrx/store';

import { Permission, Principal } from '../services/object.service';

import { DataObject } from '../models/core.models';

import {
  addPrincipal,
  removePrincipal,
  loadObjectsPermissions,
  loadObjectsPermissionsSuccess,
  togglePermissionForPrincipal,
  loadDefaultPermissions,
  loadDefaultPermissionsSuccess,
} from '../actions/permissions.actions';

import { produce } from 'immer';
import { NomObjectPermissionRole } from '../constants';
import {
  getPermissionIdsFromRole,
  getRoleFromPermissionIds,
} from '../permission.utils';

export interface PermissionsState {
  permissions: Permission[];
  objects: DataObject[];
  principals: Principal[];
  loading: boolean;
  saving: boolean;
}

export const initialState: PermissionsState = {
  objects: [],
  permissions: [],
  principals: [],
  loading: false,
  saving: false,
};

const _permissionsReducer = createReducer(
  initialState,

  on(loadObjectsPermissions, (state, { objects }) => {
    return {
      ...state,
      principals: [],
      loading: true,
      objects: objects,
    };
  }),

  on(loadDefaultPermissions, (state) => {
    return {
      ...state,
      principals: [],
      loading: true,
      objects: [],
    };
  }),

  on(
    loadObjectsPermissionsSuccess,
    loadDefaultPermissionsSuccess,
    (state, { data }) => {
      const roleRank: Record<number, number> = {
        [NomObjectPermissionRole.OWNER]: 0,
        [NomObjectPermissionRole.ADMIN]: 1,
        [NomObjectPermissionRole.EDITOR]: 2,
        [NomObjectPermissionRole.VIEWER]: 3,
        [NomObjectPermissionRole.CUSTOM]: 4,
      };

      const principalsSorted = [...data.principals].sort((a, b) => {
        const ar = roleRank[a.permission_role_id] ?? 99;
        const br = roleRank[b.permission_role_id] ?? 99;
        if (ar !== br) return ar - br;

        // tie-breakers (optional)
        if (a.is_business_admin !== b.is_business_admin) {
          return a.is_business_admin ? -1 : 1;
        }
        return (a.name ?? '').localeCompare(b.name ?? '', undefined, {
          sensitivity: 'base',
        });
      });

      return {
        ...state,
        permissions: data.permissions,
        loading: false,
        principals: principalsSorted,
      };
    },
  ),

  on(
    addPrincipal,
    produce((draft, { principal }) => {
      const exists = draft.principals.some((p) => p.id === principal.id);

      if (!exists) {
        const newPrincipal: Principal = {
          ...principal,
          permission_role_id: NomObjectPermissionRole.VIEWER,
        };
        draft.principals.push(newPrincipal);
        const rolePermissions = getPermissionIdsFromRole(
          newPrincipal.permission_role_id,
        );
        draft.permissions.forEach((perm) => {
          // add VIEWER permission by default
          if (
            perm.permission_id &&
            rolePermissions.includes(perm.permission_id)
          ) {
            perm.principals.push({
              principal_id: principal.id,
              principal_class_id: principal.object_class_id,
            });
          }
        });
      }
    }),
  ),

  on(removePrincipal, (state, { principal }) => {
    return {
      ...state,
      principals: state.principals.filter((p) => p.id !== principal.id),
    };
  }),
);

export function permissionsReducer(state: PermissionsState, action: Action) {
  return _permissionsReducer(state, action);
}
